import { useEffect, useRef, useState } from 'react';
import { Button } from '../Utility/Button';
import { toast } from 'react-hot-toast';
import Select from '../Utility/FormElements/Select';
import { useNavigate } from 'react-router-dom';
import { setRun, setStartTour, setStepIndex } from '../../Store/features/joyride/joyrideSlice';
import requestClient from '../Utility/RequestClient';
import { setChatlist } from '../../Store/features/chatlist/chatlistSlice';
import { setSelectedLanguage } from '../../Store/features/i18n/i18nSlice';
import Input from '../Utility/FormElements/Input';
import { setUserkey, setUserSettings } from '../../Store/features/userSettings/userSettingsSlice';
import AdminContainer from '../Utility/AdminContainer';
import { setChatlistClear } from '../../Store/features/environment/environmentSlice';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import useModal from '../Modal/useModal';
import Checkbox from '../Utility/FormElements/Checkbox';

function UserSettings() {
    const dispatch = useAppDispatch();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const userSettings = useAppSelector((state: RootState) => state.userSettings);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
    const [tmpSelectedLanguage, setTmpSelectedLanguage] = useState(selectedLanguage);
    const [tmpExpertViewEnabled, setTmpExpertViewEnabled] = useState(!!userSettings.expertViewEnabled);
    const requestClientRef = useRef(requestClient);
    const navigate = useNavigate();
    const modal = useModal();

    useEffect(() => {
        setTmpSelectedLanguage(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        setTmpExpertViewEnabled(!!userSettings.expertViewEnabled);
    }, [userSettings.expertViewEnabled]);

    useEffect(() => {
        const fetchChats = async () => {
            requestClientRef.current.get(`/chat/list`).then((response: any) => {
                dispatch(setChatlist(response.data.data));
            });
        };

        fetchChats();
    }, [dispatch]);

    const handleUpdateUserLanguage = async () => {
        const data = new URLSearchParams();
        data.append('language', tmpSelectedLanguage);

        requestClient.post(`/settings/updateusersettings`, data).then((response: any) => {
            dispatch(setSelectedLanguage(tmpSelectedLanguage));
            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'UserSettings' }));
        });
    };

    const handleUpdateExpertViewEnabled = async () => {
        const data = new URLSearchParams();
        data.append('expertViewEnabled', tmpExpertViewEnabled ? '1' : '0');

        requestClient.post(`/settings/updateusersettings`, data).then(() => {
            dispatch(setUserSettings({ expertViewEnabled: tmpExpertViewEnabled }));
            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'UserSettings' }));
        });
    };

    const handleUpdateUserPassword = async () => {
        if (newPassword !== newPasswordRepeat) {
            setNewPassword('');
            setNewPasswordRepeat('');
            toast.error(i18n.translate({ id: 'toast.passwords.notequal', sourceName: 'UserSettings' }));
            return;
        }

        let newUserKey = '';
        const data = new URLSearchParams();
        const hash: (content: string) => Promise<string> = async (content: string) => {
            const encoder = new TextEncoder();
            const data2 = encoder.encode(content);
            const hash2 = await crypto.subtle.digest('SHA-256', data2);
            return Array.from(new Uint8Array(hash2))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        };
        data.append('passwordHash', await hash(newPassword));
        newUserKey = await hash(newPassword + userSettings.identifier + 'kj25n4br3vqbewnkj');
        data.append('newUserKey', newUserKey);

        requestClient.post(`/settings/updateusersettings`, data).then((response: any) => {
            if (newUserKey !== '') {
                dispatch(setUserkey(newUserKey));
                document.cookie = `userkey=${newUserKey}; path=/`;
                localStorage.setItem('userPassword', newPassword);
            }
            setNewPassword('');
            setNewPasswordRepeat('');

            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'UserSettings' }));
        });
    };

    const handleLanguageChange = (value: string) => {
        setTmpSelectedLanguage(value);
    };

    const removeAllChats = () => {
        modal.deleteConfirm({
            title: i18n.translate({ id: 'chatsDelete.title', sourceName: 'UserSettings' }),
            content: i18n.translate({ id: 'chatsDelete.text', sourceName: 'UserSettings' }),
            onConfirm: () => confirmDeleteAllChats(),
            size: 'small',
        });
    };

    const confirmDeleteAllChats = async () => {
        const loading = toast.loading(
            i18n.translate({ id: 'toast.deleteAllChatsLoading', sourceName: 'UserSettings' }),
        );

        await requestClient.delete(`/chat/deleteall`);

        dispatch(setChatlistClear(true));
        dispatch(setChatlist([]));

        toast.dismiss(loading);
        toast.success(i18n.translate({ id: 'toast.allChatsDeleted', sourceName: 'UserSettings' }));
    };

    const startTour = () => {
        dispatch(setStartTour(true));
        dispatch(setRun(true));
        dispatch(setStepIndex(0));
        navigate('/chats');
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <div className="flex flex-wrap gap-4">
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'language', sourceName: 'UserSettings' })}</h4>
                        <Select value={tmpSelectedLanguage} onChange={handleLanguageChange}>
                            <option value="de">{i18n.translate({ id: 'languages.de' })}</option>
                            <option value="en">{i18n.translate({ id: 'languages.en' })}</option>
                            <option value="fr">{i18n.translate({ id: 'languages.fr' })}</option>
                        </Select>
                        <Button className="mt-3 mb-5" onClick={handleUpdateUserLanguage}>
                            {i18n.translate({ id: 'save' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'expertView.title', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'expertView.description', sourceName: 'UserSettings' })}</p>
                        <div className="mt-3">
                            <Checkbox
                                checked={tmpExpertViewEnabled}
                                label={i18n.translate({ id: 'expertView.toggle', sourceName: 'UserSettings' })}
                                onChange={(e) => setTmpExpertViewEnabled(e.target.checked)}
                            />
                        </div>
                        <Button className="mt-3 mb-5" onClick={handleUpdateExpertViewEnabled}>
                            {i18n.translate({ id: 'save' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'password.title', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'password.description', sourceName: 'UserSettings' })}</p>
                        <label className="block mt-3 mb-2">
                            {i18n.translate({ id: 'password.first', sourceName: 'UserSettings' })}
                        </label>
                        <Input value={newPassword} onChange={setNewPassword} inputType="password" />
                        <label className="block mt-3 mb-2">
                            {i18n.translate({ id: 'password.repeat', sourceName: 'UserSettings' })}
                        </label>
                        <Input value={newPasswordRepeat} onChange={setNewPasswordRepeat} inputType="password" />
                        <Button className="mt-3" onClick={handleUpdateUserPassword}>
                            {i18n.translate({ id: 'save' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'removeAllChats.headline', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'removeAllChats.text', sourceName: 'UserSettings' })}</p>
                        <Button onClick={removeAllChats}>
                            {i18n.translate({ id: 'removeAllChats.button', sourceName: 'UserSettings' })}
                        </Button>
                    </div>
                    <div className="box w-full lg:w-[calc(50%-1rem)]">
                        <h4>{i18n.translate({ id: 'startTour.headline', sourceName: 'UserSettings' })}</h4>
                        <p>{i18n.translate({ id: 'startTour.text', sourceName: 'UserSettings' })}</p>
                        <Button onClick={startTour}>
                            {i18n.translate({ id: 'startTour.button', sourceName: 'UserSettings' })}
                        </Button>
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
}

export default UserSettings;
